/**
 * Authentication Service
 *
 * Handles user authentication, registration, and token management.
 */
import { api } from '../core/api';
import {
  User,
  LoginRequest,
  RegisterRequest,
  LoginResponse,
  RegisterWithOrganizationRequest,
  RegisterWithOrganizationResponse,
} from '../types/auth';

export const authService = {
  /**
   * Register a new user
   */
  async register(data: RegisterRequest): Promise<User> {
    return api.post<User>('/auth/register', {
      email: data.email,
      full_name: data.full_name,
      password: data.password,
      role: data.role || 'ux_designer',
    });
  },

  /**
   * Login with email and password
   */
  async login(data: LoginRequest): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>('/auth/login', {
      email: data.email,
      password: data.password,
    });

    // Store access token in localStorage
    localStorage.setItem('access_token', response.access_token);

    return response;
  },

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<User> {
    return api.get<User>('/auth/me');
  },

  /**
   * Change user password (requires old password)
   */
  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    await api.post('/auth/change-password', {
      old_password: oldPassword,
      new_password: newPassword,
    });
  },

  /**
   * Set initial password (for first-time login after invitation)
   * Only available when must_change_password is true
   */
  async setInitialPassword(newPassword: string): Promise<void> {
    await api.post('/auth/set-initial-password', {
      new_password: newPassword,
    });
  },

  /**
   * Logout user (clear token and redirect to login)
   */
  logout() {
    localStorage.removeItem('access_token');
    window.location.href = '/login';
  },

  /**
   * Check if user is authenticated (has valid token)
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  },

  /**
   * Refresh the access token
   * Call this periodically while the user is active to extend the session
   */
  async refreshToken(): Promise<void> {
    const response = await api.post<{ access_token: string }>('/auth/refresh');
    localStorage.setItem('access_token', response.access_token);
  },

  /**
   * Get stored access token
   */
  getToken(): string | null {
    return localStorage.getItem('access_token');
  },

  /**
   * Register with organization (self-service signup)
   * Creates user, organization, and subscription in one step
   */
  async registerWithOrganization(
    data: RegisterWithOrganizationRequest
  ): Promise<RegisterWithOrganizationResponse> {
    const response = await api.post<RegisterWithOrganizationResponse>(
      '/auth/register-with-organization',
      data
    );

    // Store access token in localStorage
    localStorage.setItem('access_token', response.access_token);

    return response;
  },
};
